import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Star } from "lucide-react";
import { ButtonRing } from "./LoadingRing";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const useProductReviews = (slug) => useQuery({
    queryKey: ["product-reviews", slug],
    queryFn: async () => (await fetch(`${BASE}/api/reviews/${slug}`)).json(),
    enabled: !!slug,
});

const Stars = ({ value, size = "w-4 h-4", onPick = null }) => (
    <span className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} onClick={onPick ? () => onPick(n) : undefined}
                data-testid={onPick ? `review-star-${n}` : undefined}
                className={`${size} ${onPick ? "cursor-pointer" : ""} ${
                    n <= Math.round(value) ? "fill-gold text-gold" : "text-zinc-600"}`} />
        ))}
    </span>
);

export const ReviewStars = Stars;

const dateLabel = (iso) => (iso
    ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "");

const ReviewForm = ({ slug, settings, onDone }) => {
    const { customer, loginWithGoogle } = useCustomerAuth();
    const [form, setForm] = useState({ rating: 5, title: "", body: "", author_name: "", author_city: "", author_email: "" });
    const set = (patch) => setForm({ ...form, ...patch });

    const submit = useMutation({
        mutationFn: async () => {
            const res = await fetch(`${BASE}/api/reviews`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ ...form, product_slug: slug }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Could not save your review");
            return data;
        },
        onSuccess: (data) => { toast.success(data.message); onDone(); },
        onError: (e) => toast.error(e.message),
    });

    return (
        <div className="border border-white/10 bg-bg1 p-6" data-testid="review-form">
            <div className="mono text-[10px] tracking-[0.2em] text-gold">WRITE A REVIEW</div>
            <div className="mt-4 flex items-center gap-3">
                <Stars value={form.rating} size="w-6 h-6" onPick={(n) => set({ rating: n })} />
                <span className="text-sm text-zinc-500">{form.rating} of 5</span>
            </div>
            <input value={form.title} onChange={(e) => set({ title: e.target.value })}
                data-testid="review-title" placeholder="Sum it up in a few words"
                className="mt-4 w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-gold outline-none" />
            <textarea value={form.body} onChange={(e) => set({ body: e.target.value })} rows={4}
                data-testid="review-body" placeholder="How did you use it, and what did you notice? Please avoid medical claims."
                className="mt-3 w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-gold outline-none" />
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
                <input value={form.author_name} onChange={(e) => set({ author_name: e.target.value })}
                    data-testid="review-name" placeholder="Your name"
                    className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-gold outline-none" />
                <input value={form.author_city} onChange={(e) => set({ author_city: e.target.value })}
                    data-testid="review-city" placeholder="City (optional)"
                    className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-gold outline-none" />
                <input value={form.author_email} onChange={(e) => set({ author_email: e.target.value })}
                    data-testid="review-email" placeholder="Email (not shown publicly)"
                    className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-gold outline-none" />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4">
                <button onClick={() => submit.mutate()} disabled={submit.isPending}
                    className="btn-pill btn-primary" data-testid="review-submit">
                    {submit.isPending ? <ButtonRing label="SENDING…" /> : "SUBMIT REVIEW"}
                </button>
                <span className="text-xs text-zinc-500">
                    We check every review before it appears. Buying from us adds a “Verified buyer” badge.
                </span>
            </div>
            {!settings.allow_anonymous && (
                <div className="mt-3" data-testid="review-signin-note">
                    <p className="text-xs text-gold">Please sign in with the account you ordered with.</p>
                </div>
            )}
            {!customer && (
                <button onClick={loginWithGoogle} data-testid="review-google-button"
                    className="btn-pill btn-secondary mt-4">
                    CONTINUE WITH GOOGLE
                </button>
            )}
        </div>
    );
};

const QuickRate = ({ slug, onDone }) => {
    const [hover, setHover] = useState(0);
    const rate = useMutation({
        mutationFn: async (rating) => {
            const res = await fetch(`${BASE}/api/reviews/rate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ product_slug: slug, rating }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Could not save your rating");
            return data;
        },
        onSuccess: (data) => { toast.success(data.message); onDone(); },
        onError: (e) => toast.error(e.message),
    });

    return (
        <div className="inline-flex items-center gap-3" data-testid="quick-rate"
            onMouseLeave={() => setHover(0)}>
            <span className="mono text-[10px] tracking-[0.16em] text-zinc-500">RATE THIS PRODUCT</span>
            <span className="inline-flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} onMouseEnter={() => setHover(n)}
                        onClick={() => !rate.isPending && rate.mutate(n)}
                        data-testid={`quick-rate-star-${n}`}
                        className={`w-5 h-5 cursor-pointer transition-transform hover:scale-110 ${
                            n <= hover ? "fill-gold text-gold" : "text-zinc-600"}`} />
                ))}
            </span>
        </div>
    );
};

/** Real, approved reviews only — the summary here is what drives the star markup on Google. */
export const ProductReviews = ({ slug }) => {
    const qc = useQueryClient();
    const { data } = useProductReviews(slug);
    const { data: settings } = useQuery({
        queryKey: ["review-settings"],
        queryFn: async () => (await fetch(`${BASE}/api/reviews/settings`)).json(),
    });
    const [writing, setWriting] = useState(false);
    if (!data || !settings) return null;

    const canWrite = settings.allow_anonymous || settings.allow_logged_in || settings.allow_verified_buyers;

    return (
        <div data-testid="product-reviews">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight">
                        {settings.section_title}
                    </h2>
                    {data.display_average > 0 && (
                        <div className="mt-3 flex items-center gap-3" data-testid="review-summary">
                            <Stars value={data.display_average} size="w-5 h-5" />
                            <span className="text-sm text-zinc-400">
                                {data.display_average.toFixed
                                    ? data.display_average.toFixed(1) : data.display_average} out of 5
                                {data.display_count ? ` · ${data.display_count} rating${data.display_count === 1 ? "" : "s"}` : ""}
                            </span>
                        </div>
                    )}
                    <div className="mt-4">
                        <QuickRate slug={slug} onDone={() => {
                            qc.invalidateQueries({ queryKey: ["product-reviews", slug] });
                            qc.invalidateQueries({ queryKey: ["products"] });
                            qc.invalidateQueries({ queryKey: ["product", slug] });
                        }} />
                    </div>
                </div>
                {canWrite && !writing && (
                    <button onClick={() => setWriting(true)} className="btn-pill btn-secondary"
                        data-testid="review-write-button">Write a review</button>
                )}
            </div>

            {writing && (
                <div className="mt-6">
                    <ReviewForm slug={slug} settings={settings} onDone={() => {
                        setWriting(false);
                        qc.invalidateQueries({ queryKey: ["product-reviews", slug] });
                    }} />
                </div>
            )}

            {data.count === 0 ? (
                <p className="mt-6 text-zinc-500" data-testid="reviews-empty">{settings.empty_message}</p>
            ) : (
                <div className="mt-8 grid md:grid-cols-2 gap-5">
                    {data.reviews.map((r) => (
                        <div key={r.id} className="border border-white/10 bg-bg1 p-6" data-testid={`review-${r.id}`}>
                            <div className="flex items-center justify-between gap-3">
                                <Stars value={r.rating} />
                                {r.is_verified_buyer && (
                                    <span className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.14em] text-lime"
                                        data-testid={`review-verified-${r.id}`}>
                                        <BadgeCheck className="w-3.5 h-3.5" /> VERIFIED BUYER
                                    </span>
                                )}
                            </div>
                            {r.title && <div className="mt-3 font-medium">{r.title}</div>}
                            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{r.body}</p>
                            <div className="mt-4 mono text-[10px] tracking-[0.14em] text-zinc-500">
                                {r.author_name}{r.author_city ? ` · ${r.author_city}` : ""} · {dateLabel(r.created_at)}
                            </div>
                            {r.reply && (
                                <div className="mt-4 border-l-2 border-gold/50 pl-4 text-sm text-zinc-400"
                                    data-testid={`review-reply-${r.id}`}>
                                    <span className="mono text-[10px] tracking-[0.14em] text-gold">REHEALTHME</span>
                                    <p className="mt-1">{r.reply}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProductReviews;
